/**
 * Camada de focos no mapa.
 *
 * Renderizador próprio (subclasse do CanvasLayerRenderer do OL), escolhido
 * medindo (docs/focos.md):
 * - dados em vetores tipados já projetados (dataset.ts), ordenados por data;
 * - WebGL (glPoints.ts): os vértices sobem à GPU uma vez por conjunto; a cada
 *   quadro do loop só mudam os uniforms e desenha-se a faixa contígua de
 *   índices da janela de tempo (busca binária) — ~5 ms para 100 mil focos;
 * - reserva em canvas 2D (atlas de sprites + `drawImage`, pulando repetidos
 *   no mesmo pixel) quando não há WebGL ou o contexto é perdido;
 * - nos dois casos o canvas fica dentro de um `div.ol-layer`, com a mesma
 *   matriz CSS das camadas canvas do OL, e o WebGL usa
 *   `preserveDrawingBuffer: true`: a exportação (composição dos
 *   `.ol-layer canvas` após `rendercomplete`) captura os focos;
 * - detecção de clique própria, com tolerância em px e contagem de focos
 *   empilhados no mesmo ponto.
 */
import type { ReactNode } from 'react';
import Feature, { type FeatureLike } from 'ol/Feature';
import type { FrameState } from 'ol/Map';
import Point from 'ol/geom/Point';
import Layer from 'ol/layer/Layer';
import CanvasLayerRenderer from 'ol/renderer/canvas/Layer';
import type { FeatureCallback } from 'ol/renderer/vector';
import Source from 'ol/source/Source';
import { apply as applyTransform, create as createTransform, multiply, setFromArray } from 'ol/transform';
import { fatorDoZoom } from '../map/escalaDoZoom';
import { Z } from '../map/zIndex';
import { shownTimeOf, useTimeStore } from '../state/time';
import { CLASS_GOES, materializeFire, type FireDataset } from './dataset';
import { renderFirePopup } from './FirePopup';
import { areaDoFocoNaTela } from './areaDoFoco';
import { createGlContext, glDrawKey, GlPoints, type GlDrawParams } from './glPoints';
import { FLAME_BOX, FLAME_INNER_MIN_PX, FLAME_INNER_WHITE, flamePaths, strokeFlame } from './flame';
import { MAX_EXTENT, levelLook } from './look';
import { useFireStore } from './store';
import { LEVEL_COUNT, ageLevel, displayedSnapshot, indexRange, windowFor, type VisibleWindow } from './timeFilter';
import type { Fire, FireStyle } from './types';

export const FIRES_ATTRIBUTION =
  'Focos de queimada: <a href="https://terrabrasilis.dpi.inpe.br/queimadas/portal/" target="_blank" rel="noopener">INPE — Programa Queimadas</a>';

/** Parâmetros de desenho lidos dos stores no momento do render. */
export interface FireRenderParams {
  dataset: FireDataset;
  window: VisibleWindow | null;
  style: FireStyle;
}

export function currentRenderParams(): FireRenderParams {
  const { dataset, style, mode } = useFireStore.getState();
  return { dataset, style, window: windowFor(mode, displayedSnapshot(useTimeStore.getState())) };
}

// ---------------------------------------------------------------------------
// Sprites
// ---------------------------------------------------------------------------

/** Uma coluna do atlas por nível visual (quadro atual, rastro, esmaecimento, normal). */
const LEVELS = LEVEL_COUNT;

function rgba(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

function mixWhite(hex: string, k: number): string {
  const n = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16);
  const m = (c: number) => Math.round(c + (255 - c) * k);
  return `rgb(${m((n >> 16) & 255)},${m((n >> 8) & 255)},${m(n & 255)})`;
}

interface Atlas {
  key: string;
  canvas: HTMLCanvasElement | OffscreenCanvas;
  cell: number;
}

function createCanvas(w: number, h: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }
  return new OffscreenCanvas(w, h);
}

/**
 * Raio base da chama em px CSS na resolução dada: o tamanho do painel vezes o
 * fator do zoom, a regra comum a todas as camadas (`map/escalaDoZoom.ts`). No
 * WebGL é só um uniform; no canvas 2D entra na chave do atlas (sete valores).
 */
export function tamanhoNoZoom(style: FireStyle, resolution: number | undefined): number {
  return style.size * fatorDoZoom(resolution);
}

/**
 * O ponto (dx, dy em px de tela, a partir do foco; dy cresce para baixo) cai
 * na caixa da chama de raio `rs`, com a tolerância em volta?
 */
export function dentroDaChama(dx: number, dy: number, rs: number, tolerancia: number): boolean {
  return !(
    dx < rs * FLAME_BOX.left - tolerancia ||
    dx > rs * FLAME_BOX.right + tolerancia ||
    -dy < rs * FLAME_BOX.bottom - tolerancia ||
    -dy > rs * FLAME_BOX.top + tolerancia
  );
}

function buildAtlas(style: FireStyle, pixelRatio: number, size: number): Atlas {
  const r = Math.max(0.75, size) * pixelRatio;
  const cell = Math.ceil(2 * r * MAX_EXTENT + 4);
  const canvas = createCanvas(cell * LEVELS, cell * 2);
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const flame = flamePaths();
  for (let cls = 0; cls < 2; cls++) {
    const color = cls === CLASS_GOES ? style.colorGoes : style.colorOthers;
    for (let level = 0; level < LEVELS; level++) {
      const look = levelLook(cls, level);
      const cx = level * cell + cell / 2;
      const cy = cls * cell + cell / 2;
      const rc = r * look.scale;
      ctx.save();
      ctx.globalAlpha = look.alpha;
      if (look.glow > 0) {
        const g = ctx.createRadialGradient(cx, cy, rc * 0.6, cx, cy, rc * look.glow);
        g.addColorStop(0, rgba(color, look.glowAlpha));
        g.addColorStop(1, rgba(color, 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, rc * look.glow, 0, Math.PI * 2);
        ctx.fill();
      }
      // Chama (mesma forma do shader, flame.ts) com contorno escuro (contraste sobre nuvens claras).
      ctx.translate(cx, cy);
      ctx.scale(rc, rc);
      ctx.fillStyle = mixWhite(color, look.white);
      ctx.fill(flame.outer);
      if (rc >= FLAME_INNER_MIN_PX * pixelRatio) {
        ctx.fillStyle = mixWhite(color, look.white + (1 - look.white) * FLAME_INNER_WHITE);
        ctx.fill(flame.inner);
      }
      if (rc >= 1.5 * pixelRatio) {
        ctx.lineWidth = Math.max(1, 0.8 * pixelRatio) / rc;
        ctx.strokeStyle = 'rgba(0,0,0,0.55)';
        strokeFlame(ctx, flame);
      }
      ctx.restore();
    }
  }
  return { key: atlasKey(style, pixelRatio, size), canvas, cell };
}

const atlasKey = (s: FireStyle, pr: number, size: number) => `${size.toFixed(2)}|${s.colorGoes}|${s.colorOthers}|${pr}`;

// ---------------------------------------------------------------------------
// Renderizador
// ---------------------------------------------------------------------------

export interface RenderStats {
  backend: FireBackend;
  /** Focos na janela de tempo. */
  inWindow: number;
  /** Pontos desenhados neste render (0 quando o desenho foi pulado por não haver mudança). */
  drawn: number;
  ms: number;
  /** Total de desenhos WebGL desde a criação da camada. */
  draws?: number;
}

/** Backend de desenho (padrão: WebGL quando disponível; canvas 2D de reserva). */
export type FireBackend = 'webgl' | 'canvas';

class FireLayerRenderer extends CanvasLayerRenderer<FireLayer> {
  private atlas: Atlas | null = null;
  private readonly toCanvas = createTransform();
  /** Grade de pixels já desenhados (pula repetidos no mesmo pixel e sprite). */
  private stamp = new Uint32Array(0);
  private stampW = 0;
  private stampH = 0;
  private stampSeq = 0;
  /** WebGL: canvas próprio dentro de um `div.ol-layer` (formato igual ao das camadas canvas do OL). */
  private glContainer: HTMLDivElement | null = null;
  private glCanvas: HTMLCanvasElement | null = null;
  private glPoints: GlPoints | null = null;
  private glFailed = false;
  private glLost = false;
  /** Chave do último desenho WebGL (pula o redesenho quando nada mudou). */
  private glKey = '';
  /** Desenhos WebGL feitos (para medir). */
  private glDraws = 0;
  private hitCache: { ds: FireDataset; index: number; feature: Feature<Point> } | null = null;
  lastStats: RenderStats = { backend: 'canvas', inWindow: 0, drawn: 0, ms: 0 };

  override prepareFrame(): boolean {
    const p = this.getLayer().getParams();
    return p.dataset.n > 0 && p.window !== null;
  }

  override renderFrame(frameState: FrameState, target: HTMLElement | null): HTMLElement {
    if (this.getLayer().getPreferredBackend() !== 'canvas' && this.ensureGl()) return this.renderGl(frameState);
    return this.renderCanvas(frameState, target);
  }

  /** Cria o contexto WebGL na primeira vez; false se indisponível. */
  private ensureGl(): boolean {
    if (this.glPoints) return !this.glLost;
    if (this.glFailed || typeof document === 'undefined') return false;
    const canvas = document.createElement('canvas');
    const gl = createGlContext(canvas);
    if (!gl) {
      this.glFailed = true;
      return false;
    }
    try {
      this.glPoints = new GlPoints(gl);
    } catch (e) {
      console.warn('[focos] WebGL indisponível; usando canvas 2D', e);
      this.glFailed = true;
      return false;
    }
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.glLost = true;
      this.getLayer().changed();
    });
    canvas.addEventListener('webglcontextrestored', () => {
      this.glPoints?.restore();
      this.glLost = false;
      this.glKey = '';
      this.getLayer().changed();
    });
    const container = document.createElement('div');
    container.className = this.getLayer().getClassName();
    Object.assign(container.style, { position: 'absolute', width: '100%', height: '100%' });
    Object.assign(canvas.style, { position: 'absolute', left: '0', transformOrigin: 'top left' });
    container.appendChild(canvas);
    this.glContainer = container;
    this.glCanvas = canvas;
    return true;
  }

  private renderGl(frameState: FrameState): HTMLElement {
    const t0 = performance.now();
    const { dataset: ds, window: win, style } = this.getLayer().getParams();
    const layerState = frameState.layerStatesArray[frameState.layerIndex]!;
    const pr = frameState.pixelRatio;
    const canvas = this.glCanvas!;
    const W = Math.round(frameState.size[0]! * pr);
    const H = Math.round(frameState.size[1]! * pr);
    if (canvas.width !== W || canvas.height !== H) {
      canvas.width = W;
      canvas.height = H;
    }
    // Mesmo formato das camadas canvas do OL: a exportação lê esta matriz.
    const transform = `matrix(${1 / pr}, 0, 0, ${1 / pr}, 0, 0)`;
    if (canvas.style.transform !== transform) canvas.style.transform = transform;
    let lo = 0;
    let hi = 0;
    if (win) [lo, hi] = indexRange(ds.t, win.from, win.to);
    const params: GlDrawParams = {
      dataset: ds,
      window: win ?? { from: 0, to: 0, highlightFrom: null, fade: false, trail: false, step: 0, now: 0, span: 0 },
      style,
      size: tamanhoNoZoom(style, frameState.viewState.resolution),
      toPixel: frameState.coordinateToPixelTransform,
      width: W,
      height: H,
      pixelRatio: pr,
      opacity: layerState.opacity * style.opacity,
      lo,
      hi,
    };
    const key = glDrawKey(params);
    const skipped = key === this.glKey;
    if (!skipped) {
      this.glPoints!.draw(params);
      this.glKey = key;
      this.glDraws++;
    }
    this.lastStats = {
      backend: 'webgl',
      inWindow: hi - lo,
      drawn: skipped ? 0 : hi - lo,
      ms: performance.now() - t0,
      draws: this.glDraws,
    };
    return this.glContainer!;
  }

  private renderCanvas(frameState: FrameState, target: HTMLElement | null): HTMLElement {
    const t0 = performance.now();
    const layer = this.getLayer();
    const { dataset: ds, window: win, style } = layer.getParams();
    const layerState = frameState.layerStatesArray[frameState.layerIndex]!;
    const pr = frameState.pixelRatio;

    this.prepareContainer(frameState, target as HTMLElement);
    const ctx = this.context as CanvasRenderingContext2D;
    const canvas = ctx.canvas;
    const W = canvas.width;
    const H = canvas.height;
    this.preRender(ctx, frameState);

    const size = tamanhoNoZoom(style, frameState.viewState.resolution);
    if (!this.atlas || this.atlas.key !== atlasKey(style, pr, size)) this.atlas = buildAtlas(style, pr, size);
    const atlas = this.atlas;
    const cell = atlas.cell;
    const half = cell / 2;

    // coordenada do mapa → pixel do canvas
    setFromArray(this.toCanvas, this.inversePixelTransform);
    multiply(this.toCanvas, frameState.coordinateToPixelTransform);
    const [a, b, c, d, e, f] = this.toCanvas as unknown as [number, number, number, number, number, number];

    // Grade de repetidos em px CSS (recriada só quando o tamanho muda).
    const SW = Math.ceil(W / pr);
    const SH = Math.ceil(H / pr);
    if (this.stampW !== SW || this.stampH !== SH) {
      this.stampW = SW;
      this.stampH = SH;
      this.stamp = new Uint32Array(SW * SH);
      this.stampSeq = 0;
    }
    const invPr = 1 / pr;
    this.stampSeq = (this.stampSeq + 1) & 0x3ffffff;
    if (this.stampSeq === 0) {
      this.stamp.fill(0);
      this.stampSeq = 1;
    }
    const seq = this.stampSeq << 5;
    const stamp = this.stamp;

    let drawn = 0;
    let lo = 0;
    let hi = 0;
    if (win) {
      [lo, hi] = indexRange(ds.t, win.from, win.to);
      ctx.save();
      ctx.globalAlpha = layerState.opacity * style.opacity;
      const X = ds.x;
      const Y = ds.y;
      const T = ds.t;
      const C = ds.cls;
      const img = atlas.canvas as CanvasImageSource;
      const margin = half;
      for (let i = lo; i < hi; i++) {
        const x = X[i]!;
        const y = Y[i]!;
        const px = a * x + c * y + e;
        const py = b * x + d * y + f;
        if (px < -margin || py < -margin || px > W + margin || py > H + margin) continue;
        const cls = C[i]!;
        const level = ageLevel(T[i]!, win);
        const sprite = cls * LEVELS + level;
        const ix = (px * invPr) | 0;
        const iy = (py * invPr) | 0;
        if (ix >= 0 && iy >= 0 && ix < SW && iy < SH) {
          const k = iy * SW + ix;
          const v = seq | sprite;
          if (stamp[k] === v) continue;
          stamp[k] = v;
        }
        ctx.drawImage(img, level * cell, cls * cell, cell, cell, Math.round(px - half), Math.round(py - half), cell, cell);
        drawn++;
      }
      ctx.restore();
    }
    this.postRender(ctx, frameState);
    this.lastStats = { backend: 'canvas', inWindow: hi - lo, drawn, ms: performance.now() - t0 };
    return this.container;
  }

  override disposeInternal(): void {
    this.glPoints?.dispose();
    this.glPoints = null;
    this.glContainer = null;
    this.glCanvas = null;
    super.disposeInternal();
  }

  override forEachFeatureAtCoordinate<T>(
    coordinate: number[],
    frameState: FrameState,
    hitTolerance: number,
    callback: FeatureCallback<T>,
  ): T | undefined {
    const layer = this.getLayer();
    const { dataset: ds, window: win, style } = layer.getParams();
    if (!win || ds.n === 0) return undefined;
    const m = frameState.coordinateToPixelTransform;
    const [cx, cy] = applyTransform(m, coordinate.slice(0, 2));
    const [lo, hi] = indexRange(ds.t, win.from, win.to);
    const [a, b, c, d, e, f] = m as unknown as [number, number, number, number, number, number];
    // O mesmo tamanho do desenho: a caixa do clique acompanha a chama no zoom.
    const size = tamanhoNoZoom(style, frameState.viewState.resolution);
    let best = -1;
    let bestD = Infinity;
    let stack = 0;
    for (let i = hi - 1; i >= lo; i--) {
      const px = a * ds.x[i]! + c * ds.y[i]! + e;
      const py = b * ds.x[i]! + d * ds.y[i]! + f;
      const dx = px - cx!;
      const dy = py - cy!;
      const d2 = dx * dx + dy * dy;
      const level = ageLevel(ds.t[i]!, win);
      // Caixa da chama (y do mapa para cima = y da tela para baixo) + tolerância.
      const rs = size * levelLook(ds.cls[i]!, level).scale;
      if (!dentroDaChama(dx, dy, rs, hitTolerance)) continue;
      stack++;
      if (d2 < bestD - 0.25) {
        bestD = d2;
        best = i;
      }
    }
    if (best < 0) return undefined;
    // Reaproveita a feature (o app-core pode consultar a cada movimento do mouse).
    let feature = this.hitCache?.ds === ds && this.hitCache.index === best ? this.hitCache.feature : null;
    if (!feature) {
      const fire = materializeFire(ds, best);
      feature = new Feature({ geometry: new Point([ds.x[best]!, ds.y[best]!]) });
      feature.setId(fire.id);
      feature.set('fire', fire);
      this.hitCache = { ds, index: best, feature };
    }
    feature.set('stack', stack, true);
    return callback(feature, layer, feature.getGeometry()!);
  }
}

// ---------------------------------------------------------------------------
// Camada
// ---------------------------------------------------------------------------

export class FireLayer extends Layer<Source, FireLayerRenderer> {
  private readonly paramsFn: () => FireRenderParams;

  constructor(paramsFn: () => FireRenderParams = currentRenderParams) {
    super({
      source: new Source({ attributions: FIRES_ATTRIBUTION }),
      // Classe própria: nenhuma outra camada reaproveita (e apaga) este canvas.
      className: 'ol-layer amaview-fires',
      zIndex: Z.FIRES,
      properties: { id: 'fires', title: 'Focos de queimada' },
    });
    this.paramsFn = paramsFn;
    this.setZIndex(Z.FIRES);
    // A ficha não cobre o foco nem o círculo de 375 m (e o miolo dos imóveis do CAR):
    // abre ao lado, ligada a ele por uma linha, e pode ser arrastada (map/popupArea).
    // A ficha de abas muda de altura ao trocar de aba: presa pelo topo, as abas ficam paradas.
    this.set('popupAbaixo', true);
    // Arrastável em qualquer tela, como a do rastreio: presa pelo topo, ela cresce para baixo.
    this.set('popupMovel', true);
    this.set('popupEvitar', (feature: FeatureLike) => {
      const fire = feature.get('fire') as Fire | undefined;
      return fire ? areaDoFocoNaTela(fire.lat, fire.lon) : null;
    });
    this.set('popup', (feature: FeatureLike): ReactNode | null => {
      const fire = feature.get('fire') as Fire | undefined;
      return fire ? renderFirePopup(fire, (feature.get('stack') as number) ?? 1) : null;
    });
  }

  getParams(): FireRenderParams {
    return this.paramsFn();
  }

  /** Força um backend (medições/depuração). Padrão: WebGL quando disponível. */
  setPreferredBackend(backend: FireBackend | 'auto'): void {
    this.set('backend', backend);
    this.changed();
  }

  getPreferredBackend(): FireBackend | 'auto' {
    return (this.get('backend') as FireBackend | undefined) ?? 'auto';
  }

  override createRenderer(): FireLayerRenderer {
    return new FireLayerRenderer(this);
  }

  /** Estatísticas do último desenho (para medir desempenho). */
  getRenderStats(): RenderStats | null {
    return this.getRenderer()?.lastStats ?? null;
  }
}

/**
 * Cria a camada e a mantém sincronizada com o loop e com o estado dos focos
 * (cada mudança só pede um novo render; nada é recalculado por quadro além
 * da busca binária e do desenho).
 */
export function createFireLayer(): { layer: FireLayer; dispose: () => void } {
  const layer = new FireLayer();
  const unsubs = [
    useTimeStore.subscribe((s, p) => {
      if (s.range !== p.range || s.frames !== p.frames) layer.changed();
      // No modo "Todo o período" os focos não dependem do quadro atual.
      else if (shownTimeOf(s) !== shownTimeOf(p) && useFireStore.getState().mode !== 'all') layer.changed();
    }),
    useFireStore.subscribe((s, p) => {
      if (s.dataset !== p.dataset || s.style !== p.style || s.mode !== p.mode) layer.changed();
    }),
  ];
  return {
    layer,
    dispose: () => {
      unsubs.forEach((u) => u());
      layer.dispose();
    },
  };
}
